<script>
	import { onMount } from 'svelte';
	import { authAPI, passwordStrength } from './api.js';
	import { currentUser } from './store.js';
	import Icon from './lib/Icon.svelte';

	// 'login' | 'register' | 'forgot' | 'reset'
	let mode = $state('login');
	let email = $state('');
	let password = $state('');
	let name = $state('');
	let company = $state('');
	let error = $state('');
	let notice = $state('');
	let loading = $state(false);
	let showPassword = $state(false);
	let resetToken = $state('');

	let strength = $derived(passwordStrength(password));

	onMount(async () => {
		const params = new URLSearchParams(window.location.search);
		const rt = params.get('reset_token');
		const vt = params.get('verify_token');
		if (rt) {
			resetToken = rt;
			mode = 'reset';
		} else if (vt) {
			try {
				const res = await authAPI.verifyEmail(vt);
				notice = res.message || 'Correo verificado.';
			} catch (err) {
				error = err.message || 'No se pudo verificar el correo.';
			}
			window.history.replaceState({}, '', '/');
		}
	});

	function switchMode(m) {
		mode = m;
		error = '';
		notice = '';
	}

	async function handleSubmit(e) {
		e.preventDefault();
		error = '';
		notice = '';
		loading = true;
		try {
			if (mode === 'login') {
				const data = await authAPI.login(email, password);
				currentUser.set(data.user);
				window.location.reload();
			} else if (mode === 'register') {
				await authAPI.register(name, email, password, company);
				// Auto-login after self-service signup
				const data = await authAPI.login(email, password);
				currentUser.set(data.user);
				window.location.reload();
			} else if (mode === 'forgot') {
				const res = await authAPI.forgotPassword(email);
				notice = res.message;
			} else if (mode === 'reset') {
				const res = await authAPI.resetPassword(resetToken, password);
				notice = res.message;
				window.history.replaceState({}, '', '/');
				setTimeout(() => switchMode('login'), 1500);
			}
		} catch (err) {
			error = err.message || 'Algo salió mal. Intenta de nuevo.';
		} finally {
			loading = false;
		}
	}

	const TITLES = {
		login: ['Bienvenido de nuevo', 'Inicia sesión para gestionar tus conversaciones y agentes IA.'],
		register: ['Crea tu cuenta', 'Prueba Mangu gratis por 14 días. Sin tarjeta.'],
		forgot: ['Recuperar contraseña', 'Te enviaremos un enlace para restablecerla.'],
		reset: ['Nueva contraseña', 'Elige una contraseña segura para tu cuenta.']
	};
	const SUBMIT_LABELS = {
		login: 'Iniciar sesión',
		register: 'Crear cuenta',
		forgot: 'Enviar enlace',
		reset: 'Guardar contraseña'
	};
</script>

<div class="login-page">
	<div class="login-card">
		<div class="login-brand">
			<div class="brand-mark">M</div>
			<span class="brand-name">Mangu</span>
		</div>

		<h1 class="login-title">{TITLES[mode][0]}</h1>
		<p class="login-sub">{TITLES[mode][1]}</p>

		{#if error}
			<div class="error-banner">
				<Icon name="alert-circle" size={16} />
				<span>{error}</span>
			</div>
		{/if}
		{#if notice}
			<div class="notice-banner">
				<Icon name="check-circle" size={16} />
				<span>{notice}</span>
			</div>
		{/if}

		<form onsubmit={handleSubmit}>
			{#if mode === 'register'}
				<div class="field">
					<label for="name">Tu nombre</label>
					<input type="text" id="name" bind:value={name} required
						autocomplete="name" placeholder="Ana Mora" />
				</div>
				<div class="field">
					<label for="company">Nombre de tu negocio</label>
					<input type="text" id="company" bind:value={company}
						autocomplete="organization" placeholder="Tours CR" />
				</div>
			{/if}

			{#if mode !== 'reset'}
				<div class="field">
					<label for="email">Email</label>
					<input
						type="email"
						id="email"
						bind:value={email}
						required
						autocomplete="email"
						placeholder="tu@empresa.com"
					/>
				</div>
			{/if}

			{#if mode !== 'forgot'}
				<div class="field">
					<label for="password">{mode === 'reset' ? 'Nueva contraseña' : 'Contraseña'}</label>
					<div class="password-wrap">
						<input
							type={showPassword ? 'text' : 'password'}
							id="password"
							bind:value={password}
							required
							autocomplete={mode === 'login' ? 'current-password' : 'new-password'}
							placeholder="••••••••"
						/>
						<button
							type="button"
							class="pw-toggle"
							onclick={() => (showPassword = !showPassword)}
							aria-label="Mostrar contraseña"
						>
							<Icon name={showPassword ? 'eye-off' : 'eye'} size={16} />
						</button>
					</div>
					{#if password && mode !== 'login'}
						<div class="pw-strength">
							<div class="pw-bar">
								<div class="pw-fill" style="width:{(strength.score / 5) * 100}%; background:{strength.color}"></div>
							</div>
							<span class="pw-label" style="color:{strength.color}">{strength.label}</span>
						</div>
					{/if}
				</div>
			{/if}

			<button type="submit" class="btn btn-primary btn-block" disabled={loading}>
				{#if loading}
					<span class="dot-spinner"></span>
					<span>Un momento...</span>
				{:else}
					<span>{SUBMIT_LABELS[mode]}</span>
					<Icon name="arrow-right" size={16} />
				{/if}
			</button>
		</form>

		<div class="login-foot">
			{#if mode === 'login'}
				<button class="link-like" onclick={() => switchMode('forgot')}>¿Olvidaste tu contraseña?</button>
				<span class="sep">·</span>
				<button class="link-like" onclick={() => switchMode('register')}>Crear cuenta gratis</button>
			{:else}
				<button class="link-like" onclick={() => switchMode('login')}>← Volver a iniciar sesión</button>
			{/if}
		</div>
	</div>

	<div class="login-aside">
		<div class="aside-content">
			<div class="badge badge-brand">
				<Icon name="sparkles" size={12} />
				<span style="margin-left:4px">AI Agents</span>
			</div>
			<h2>Atiende a tus clientes con agentes que aprenden, no que solo responden.</h2>
			<p>CRM multicanal con WhatsApp, web chat, agentes IA y handoff a humanos en tiempo real.</p>
			<ul class="feat-list">
				<li><Icon name="check" size={14} /> <span>Pipeline de leads visual</span></li>
				<li><Icon name="check" size={14} /> <span>Agentes IA con base de conocimiento</span></li>
				<li><Icon name="check" size={14} /> <span>Multi-tenant, multi-canal</span></li>
				<li><Icon name="check" size={14} /> <span>Estadísticas en tiempo real</span></li>
			</ul>
		</div>
	</div>
</div>

<style>
	.login-page {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		min-height: 100vh;
		background: var(--surface-1);
	}

	.login-card {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 48px 56px;
		max-width: 480px;
		width: 100%;
		margin: 0 auto;
	}

	.login-brand {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 56px;
	}
	.brand-mark {
		width: 36px; height: 36px;
		border-radius: 8px;
		background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
		display: flex; align-items: center; justify-content: center;
		font-weight: 700; color: #fff; font-size: 18px;
	}
	.brand-name { font-weight: 700; font-size: 18px; color: var(--text-1); letter-spacing: -0.01em; }

	.login-title { font-size: 26px; margin-bottom: 8px; letter-spacing: -0.015em; }
	.login-sub { color: var(--text-3); font-size: 14px; margin-bottom: 28px; }

	.error-banner {
		display: flex; align-items: center; gap: 8px;
		background: var(--danger-soft);
		color: var(--danger);
		padding: 10px 12px;
		border-radius: 8px;
		font-size: 13px;
		margin-bottom: 16px;
	}
	.notice-banner {
		display: flex; align-items: center; gap: 8px;
		background: var(--success-soft);
		color: var(--success);
		padding: 10px 12px;
		border-radius: 8px;
		font-size: 13px;
		margin-bottom: 16px;
	}
	.link-like {
		color: var(--accent);
		font-size: 13px;
		cursor: pointer;
	}
	.link-like:hover { color: var(--accent-hover); text-decoration: underline; }
	.sep { color: var(--text-3); margin: 0 8px; }

	.field { margin-bottom: 16px; }

	.password-wrap { position: relative; }
	.pw-toggle {
		position: absolute;
		right: 8px; top: 50%; transform: translateY(-50%);
		width: 30px; height: 30px;
		display: flex; align-items: center; justify-content: center;
		color: var(--text-3);
		border-radius: 6px;
	}
	.pw-toggle:hover { color: var(--text-1); background: var(--surface-2); }

	.pw-strength {
		margin-top: 6px;
		display: flex; align-items: center; gap: 8px;
	}
	.pw-bar {
		flex: 1;
		height: 4px;
		background: var(--surface-2);
		border-radius: 2px;
		overflow: hidden;
	}
	.pw-fill {
		height: 100%;
		border-radius: 2px;
		transition: width 200ms var(--ease), background 200ms var(--ease);
	}
	.pw-label { font-size: 11px; font-weight: 600; }

	.btn-block {
		width: 100%;
		justify-content: center;
		padding: 11px 16px;
		font-size: 14px;
		margin-top: 8px;
	}

	.dot-spinner {
		width: 14px; height: 14px;
		border: 2px solid rgba(255,255,255,0.4);
		border-top-color: #fff;
		border-radius: 50%;
		animation: spin 600ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	.login-foot {
		margin-top: 20px;
		font-size: 13px;
		text-align: center;
	}

	/* ===== Aside (right) ===== */
	.login-aside {
		background: linear-gradient(135deg, #1f2937 0%, #0f172a 100%);
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 56px;
		position: relative;
		overflow: hidden;
	}
	.login-aside::before {
		content: '';
		position: absolute;
		inset: -50%;
		background: radial-gradient(circle at 30% 20%, rgba(37, 211, 102, 0.18), transparent 50%),
		            radial-gradient(circle at 70% 80%, rgba(79, 109, 245, 0.18), transparent 50%);
		pointer-events: none;
	}
	.aside-content {
		position: relative;
		max-width: 460px;
	}
	.aside-content :global(.badge) {
		display: inline-flex;
		align-items: center;
		font-size: 11px;
		padding: 4px 10px;
		margin-bottom: 24px;
	}
	.aside-content h2 {
		font-size: 30px;
		font-weight: 600;
		color: #fff;
		line-height: 1.25;
		letter-spacing: -0.02em;
		margin-bottom: 16px;
	}
	.aside-content p {
		color: rgba(255, 255, 255, 0.7);
		font-size: 15px;
		line-height: 1.6;
		margin-bottom: 28px;
	}
	.feat-list {
		list-style: none;
		padding: 0; margin: 0;
		display: flex; flex-direction: column;
		gap: 10px;
	}
	.feat-list li {
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 14px;
		color: rgba(255, 255, 255, 0.85);
	}
	.feat-list :global(svg) {
		color: var(--brand);
		background: rgba(37, 211, 102, 0.15);
		border-radius: 50%;
		padding: 3px;
		box-sizing: content-box;
	}

	@media (max-width: 960px) {
		.login-page { grid-template-columns: 1fr; }
		.login-aside { display: none; }
	}
</style>
